import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { get, useForm } from 'react-hook-form';
import { z } from 'zod';
import i18n from '@/Config/app/i18n';
import { AlertDto } from '@/Domain/api/Conversation/dto/Alert.dto';
import { METADATA_CONDITION_OPERATORS } from '@/Domain/api/Shared/dto/MetadataRunCondition.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import {
  ALERT_RUN_CONDITIONS_MAX_COUNT,
  areAlertRunConditionsComplete,
} from '@/Domain/helper/alertRunCondition.helper';
import {
  METADATA_CONDITION_VALUE_MAX_LENGTH,
  metadataConditionValuesSchema,
  validateMetadataConditionOperand,
} from '@/Domain/helper/metadataRunCondition.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useConversationAnalysisAlertDelete } from '@/Domain/hooks/Conversation/useConversationAnalysisAlertDelete.hook';
import { useConversationAnalysisAlertUpdate } from '@/Domain/hooks/Conversation/useConversationAnalysisAlertUpdate.hook';

const alertRunConditionSchema = z
  .object({
    id: z.number().or(z.string()),
    metadata_key: z.string().max(100),
    operator: z.enum(METADATA_CONDITION_OPERATORS),
    value: z.string().max(METADATA_CONDITION_VALUE_MAX_LENGTH).nullable(),
    values: metadataConditionValuesSchema(),
  })
  .superRefine(validateMetadataConditionOperand);

export const alertSchema = z.object({
  name: z
    .string()
    .min(1, i18n.t('insights_page:manage_alerts.form.validation.min_name'))
    .max(100, i18n.t('insights_page:manage_alerts.form.validation.max_name')),
  description: z
    .string()
    .min(1, i18n.t('insights_page:manage_alerts.form.validation.min_description'))
    .max(500, i18n.t('insights_page:manage_alerts.form.validation.max_description')),
  is_active: z.boolean(),
  run_conditions: z
    .array(alertRunConditionSchema)
    .max(ALERT_RUN_CONDITIONS_MAX_COUNT, {
      message: i18n.t('insights_page:manage_alerts.form.validation.too_many_conditions'),
    })
    .refine(areAlertRunConditionsComplete, {
      message: i18n.t('insights_page:manage_alerts.form.validation.incomplete_condition'),
    }),
});

export type AlertFormValues = z.infer<typeof alertSchema>;

export const useAlertForm = (alert: AlertDto) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const { handleUpdateAlert, isPending } = useConversationAnalysisAlertUpdate();
  const { handleDeleteAlert } = useConversationAnalysisAlertDelete();
  const { capture } = usePostHogAnalytics();

  useEffect(() => {
    if (!alert.name) {
      setIsEditMode(true);
    }
  }, [alert.name]);

  const form = useForm<AlertFormValues>({
    mode: 'onChange',
    values: {
      name: alert.name,
      description: alert.description,
      is_active: alert.is_active,
      run_conditions: alert.run_conditions,
    },
    resolver: zodResolver(alertSchema),
  });

  const handleDelete = async (alertId?: number) => {
    if (!alertId) {
      return;
    }
    try {
      await handleDeleteAlert(alertId);
      capture(PostHogEventEnum.AlertDeleted, { alert_id: alertId });
    } catch {
      // onError toast already surfaces failure; swallow so a failed delete isn't captured as success.
    }
  };

  const handleSubmit = async (values: AlertFormValues) => {
    try {
      await handleUpdateAlert({ ...values, id: alert.id });
      capture(PostHogEventEnum.AlertUpdated, {
        alert_id: alert.id,
        name_length: values.name.length,
        description_length: values.description.length,
      });
      setIsEditMode(false);
    } catch (error) {
      form.setError('name', {
        message: get(error, 'response.data.errors.root[0].message'),
      });
    }
  };

  const handleToggleActive = async (next: boolean) => {
    form.setValue('is_active', next);
    if (alert.name) {
      await handleUpdateAlert({
        id: alert.id,
        is_active: next,
      });
      capture(PostHogEventEnum.AlertActiveToggled, {
        alert_id: alert.id,
        is_active: next,
      });
    }
  };

  const handleReset = () => {
    form.reset();
    form.clearErrors();
  };

  return {
    form,
    handleSubmit,
    handleDelete,
    handleToggleActive,
    handleReset,
    isEditMode,
    isUpdatePending: isPending,
    setIsEditMode,
  };
};
